import { Box, Button, Snackbar, Typography } from "@mui/joy";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { capitalizeText } from "../../../lib/utils";
import { ILotto } from "../../../models";
import PackingListToPrint from "./dialogs/print/packingListToPrint";
import DettaglioPrintDialog from "./dialogs/print/printDialog";

interface ILottoInformationsProps {
  lotto: ILotto | null;
  onBack: () => void;
}

export default function LottoInformations({
  lotto,
  onBack,
}: ILottoInformationsProps) {
  const { i18n, t } = useTranslation();
  const componentRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showPrintable, setShowPrintable] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const numeroLastre = lotto?.dettagli?.reduce(
    (prev, dett) => dett.pezzi + prev,
    0
  );
  const hasOptioned = useMemo(
    () =>
      !!lotto?.dettagli?.some(
        (item) => item.idDisponibilita && item.idDisponibilita > 1
      ),
    [lotto]
  );
  const nomeMateriale = useMemo(
    () =>
      i18n.language === "en" && lotto?.materiale?.nomeEn
        ? lotto.materiale.nomeEn
        : lotto?.materiale?.nome ?? "",
    [lotto, i18n]
  );
  const nomeQualita = useMemo(
    () =>
      i18n.language === "en" && lotto?.qualita?.nomeEn
        ? lotto.qualita.nomeEn
        : lotto?.qualita?.nome ?? "",
    [lotto, i18n]
  );
  const openPackingList = () => {
    setIsOpen(true);
  };

  const getPdf = () => {
    setShowPrintable(true);
  };

  const handleCopy = async () => {
    const lottoID = lotto?.id?.toString() ?? "";
    const lang = i18n.language;
    const params = new URLSearchParams({
      lang,
      lottoID,
    });
    const url = "https://www.petris.it/magazzino/?" + params.toString();
    await navigator.clipboard.writeText(url);
    setOpenSnackbar(true);

    // chiude automaticamente dopo 1.5 sec
    setTimeout(() => setOpenSnackbar(false), 1500);
  };

  const generatePdf = useCallback(async () => {
    // 1. Clona il nodo da stampare
    const node = componentRef.current! as HTMLElement;

    // 2. Crei un wrapper off-screen

    // 4. Applica la classe print-forzata SOLO al clone
    node.classList.add("force-print");

    // 5. Inserisci il clone dentro il wrapper
    const canvas = await html2canvas(componentRef.current!, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`packing_list_${lotto?.codice}.pdf`);
  }, [lotto?.codice]);

  useLayoutEffect(() => {
    if (!showPrintable) return;
    if (!componentRef.current) return;

    const run = async () => {
      await generatePdf();
      setShowPrintable(false);
    };

    run();
  }, [showPrintable, generatePdf]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        position: "relative",
        pt: 2,
        pb: 1,
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ px: 0, pt: 1, width: "100%", boxSizing: "border-box" }}>
        <Typography
          level="body-md"
          onClick={onBack}
          sx={{
            fontSize: "13px",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          &lt; {t("Back")}
        </Typography>
        <Box sx={{ display: "flex", py: 2 }}>
          <Typography sx={{ mt: 2 }} level="h2">
            {lotto?.codice}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", py: 1 }}>
          <Typography sx={{ color: "#b3b3b3" }}>
            {capitalizeText(t("MaterialType"))}:
          </Typography>
          <Typography sx={{ ml: 2, color: "#8b8b8c" }}>
            {capitalizeText(nomeMateriale)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", py: 1 }}>
          <Typography sx={{ color: "#b3b3b3" }}>
            {capitalizeText(t("Material"))}:
          </Typography>
          <Typography sx={{ ml: 2, color: "#8b8b8c" }}>
            {capitalizeText(nomeQualita)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", py: 1 }}>
          <Typography sx={{ color: "#b3b3b3" }}>
            {capitalizeText(t("Slabs"))}:
          </Typography>
          <Box sx={{ display: "flex", ml: 2 }}>
            <Typography sx={{ color: "#8b8b8c" }}>
              {t("TotalShort")}&nbsp;
            </Typography>
            <Typography sx={{ color: "#b3b3b3" }}>{numeroLastre}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", ml: 2 }}>
          <Box sx={{}}>
            {lotto?.dettagli?.map((dett, index) => {
              const nomeLavorazione =
                i18n.language === "en" && dett.stato_lavorazione?.nomeEn
                  ? dett.stato_lavorazione.nomeEn
                  : dett.stato_lavorazione?.nome ?? "";
              return (
                <Box key={index} sx={{ display: "flex", gap: 4 }}>
                  <Box sx={{ display: "flex", flex: 1 }}>
                    <Typography sx={{ color: "#8b8b8c" }}>
                      {t("QuantityShort")}&nbsp;
                    </Typography>
                    <Typography
                      sx={{ color: "#b3b3b3" }}
                    >{`${dett.pezzi}`}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", flex: 2 }}>
                    <Typography sx={{ color: "#8b8b8c" }}>cm&nbsp;</Typography>
                    <Typography sx={{ color: "#b3b3b3" }}>
                      {`${dett.misX} x ${dett.misY} x ${dett.misZ}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flex: 2 }}>
                    <Typography sx={{ color: "#8b8b8c" }}>
                      {t("FinishShort")}&nbsp;
                    </Typography>
                    <Typography sx={{ color: "#b3b3b3" }}>{`${capitalizeText(
                      nomeLavorazione
                    )}`}</Typography>
                  </Box>
                  {!!dett.idDisponibilita && dett.idDisponibilita > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50px",
                          backgroundColor: "#fbdb47",
                        }}
                      ></Box>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        {hasOptioned && (
          <Box sx={{ marginTop: 4, display: "flex" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: "16px",
                  height: "16px",
                  borderRadius: "50px",
                  backgroundColor: "#fbdb47",
                }}
              ></Box>
            </Box>

            <Typography
              level="body-xs"
              sx={{ color: "#8b8b8c", marginLeft: 2 }}
            >
              {capitalizeText(t("Optioned"))}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          px: 4,
          pt: 4,
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          sx={{
            display: "none",
            "@media screen and (min-width: 1024px)": {
              display: "block",
            },
          }}
          onClick={openPackingList}
        >
          {capitalizeText(t("RequestPackingList"))}
        </Button>
        <Button
          sx={{
            display: "block",
            "@media screen and (min-width: 1024px)": {
              display: "none",
            },
          }}
          onClick={getPdf}
        >
          {capitalizeText(t("RequestPackingList"))}
        </Button>
      </Box>
      <Box
        sx={{
          px: 4,
          pt: 1,
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button onClick={handleCopy}>{capitalizeText(t("ShareLink"))}</Button>
      </Box>

      {lotto && (
        <DettaglioPrintDialog
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          lotto={lotto}
        ></DettaglioPrintDialog>
      )}
      {lotto && showPrintable && (
        <Box
          sx={{
            position: "fixed",
            top: "-9999px",
            left: "-9999px",
          }}
        >
          <PackingListToPrint ref={componentRef} lotto={lotto} />
        </Box>
      )}
      <Snackbar
        variant="soft"
        color="success"
        open={openSnackbar}
        sx={{
          position: "fixed",
          top: "1rem",
          right: "1rem",
          left: "auto",
          bottom: "auto",
        }}
        onClose={() => setOpenSnackbar(false)}
      >
        {capitalizeText(t("LinkCopiedToClipboard"))}
      </Snackbar>
    </Box>
  );
}
